import React, { useRef, useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Paper,
    Grid,
    Dialog,
    DialogContent,
    DialogActions,
    Tabs,
    Tab
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SaveIcon from '@mui/icons-material/Save';
import AttachmentUploader from './attachmentUploader';
import AppDialogTitle from '../ui/appDialogTitle';
import { ACCEPTED_IMAGE_FILE_FORMAT, formatFileSize } from '../../utils';

const MAX_FILE_SIZE = 500 * 1024; // 500KB for signature
const MAX_NO_OF_FILES = 1;
const SAFE_ZONE_PADDING = 10; // 20px padding from each edge

/**
 * SignatureDialog Component
 * 
 * A dialog component for capturing signatures through drawing or file upload.
 * Provides a clean interface for signature capture with tabs for different input methods.
 * 
 * @param {boolean} open - Controls dialog visibility
 * @param {function} onClose - Callback when dialog is closed
 * @param {function} onSignatureSave - Callback when signature is accepted, receives signature data
 * @param {boolean} disabled - Disables signature capture functionality
 */
const SignatureDialog = ({ open, onClose, onSignatureSave, disabled = false }) => {
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [hasDrawnSignature, setHasDrawnSignature] = useState(false);

    // Function to draw the safe zone outline
    const drawSafeZone = (ctx) => {
        // Save current context settings
        ctx.save();
        
        // Draw safe zone outline
        ctx.setLineDash([5, 5]); // Set a dashed line
        ctx.strokeStyle = '#aaa'; // Light gray for the safe zone outline
        ctx.lineWidth = 1;

        ctx.strokeRect(
            SAFE_ZONE_PADDING,
            SAFE_ZONE_PADDING,
            ctx.canvas.width - 2 * SAFE_ZONE_PADDING,
            ctx.canvas.height - 2 * SAFE_ZONE_PADDING
        );
        
        // Restore context settings
        ctx.restore();
    };

    /**
     * Initialize the signature canvas when it is available and visible.
     *
     * - Clears the canvas to ensure a transparent background for PNG exports.
     * - Calls drawSafeZone to render any visual guides (safe/drawable area).
     * - Sets up drawing properties (strokeStyle, lineWidth, lineCap, lineJoin) for ink input.
     *
     * Important: this initialization should happen after the component/dialog becomes visible
     * (for example, when a transition prop signals the open animation has finished). Initializing
     * the canvas while the element is hidden or mid-transition can yield incorrect canvas dimensions
     * or device pixel ratio, producing zero-sized, clipped, or blurry drawings. Running this
     * function after the transition guarantees the DOM has its final layout and styles so that
     * getContext and subsequent drawing operations use the correct pixel buffer.
     *
     * @returns {void}
     */
    const initializeCanvas = () => {
        if (canvasRef.current) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            
            // Clear canvas (transparent background for PNG)
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            
            // Draw the safe zone outline
            drawSafeZone(ctx);
            
            // Configure drawing properties
            ctx.strokeStyle = '#000000';
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
        }
    };

    // Handle mouse down event to start drawing
    const startDrawing = (e) => {
        if (disabled) return;
        setIsDrawing(true);
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const ctx = canvas.getContext('2d');
        
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        // Check if starting point is within the safe zone
        const isWithinSafeZone = x >= SAFE_ZONE_PADDING &&
                                 x <= canvas.width - SAFE_ZONE_PADDING &&
                                 y >= SAFE_ZONE_PADDING &&
                                 y <= canvas.height - SAFE_ZONE_PADDING;

        if (isWithinSafeZone) {
            ctx.beginPath();
            ctx.moveTo(x, y);
        } else {
            // If starting outside the safe zone, stop drawing
            stopDrawing();
        }
    };

    // Handle mouse move event to draw lines
    const draw = (e) => {
        if (!isDrawing || disabled) return;
        
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const ctx = canvas.getContext('2d');
        
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        // Only draw if the current point is within the safe zone
        const isWithinSafeZone = x >= SAFE_ZONE_PADDING &&
                                 x <= canvas.width - SAFE_ZONE_PADDING &&
                                 y >= SAFE_ZONE_PADDING &&
                                 y <= canvas.height - SAFE_ZONE_PADDING;
        
        if (isWithinSafeZone) {
            ctx.lineTo(x, y);
            ctx.stroke();
            setHasDrawnSignature(true);
        } else {
            // Stop drawing immediately if the user moves outside the safe zone
            stopDrawing();
        }
    };

    // Handle mouse up event to stop drawing
    const stopDrawing = () => {
        setIsDrawing(false);
    };

    // Clear the canvas and reset drawing state
    const clearCanvas = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        
        // Clear canvas (transparent background)
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Redraw the safe zone outline
        drawSafeZone(ctx);
        
        setHasDrawnSignature(false);
    };

    // Convert canvas to signature data and trigger save callback
    const acceptSignature = () => {
        const canvas = canvasRef.current;
        
        // Ensure canvas has content before saving
        if (!hasDrawnSignature) {
            return;
        }
        
        // Create a temporary canvas without safe zone for saving
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = canvas.width;
        tempCanvas.height = canvas.height;
        const tempCtx = tempCanvas.getContext('2d');
        
        // Copy only the signature (black strokes) to temp canvas
        const imageData = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Create new image data with only signature strokes (filter out safe zone)
        for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            
            // Keep only black strokes (signature), make everything else transparent
            if (r === 0 && g === 0 && b === 0) {
                // Keep black pixels (signature)
            } else {
                // Make non-black pixels transparent
                data[i + 3] = 0;
            }
        }
        
        tempCtx.putImageData(imageData, 0, 0);

        tempCanvas.toBlob(async (blob) => {
            if (!blob) {
                console.error('Failed to create blob from canvas');
                return;
            }
            
            // Convert blob to array buffer for backend processing
            const arrayBuffer = await blob.arrayBuffer();
            const buffer = Array.from(new Uint8Array(arrayBuffer));
            
            const signatureData = {
                originalname: `signature.png`,
                file: buffer,
                isHandDrawn: true
            };
            
            // Call parent callback with signature data
            onSignatureSave([signatureData]);
            
            // Close dialog after successful save
            onClose();
        }, 'image/png');
    };

    // Handle uploaded signature files
    const handleUploadedSignature = (files) => {
        onSignatureSave(files);
        onClose(); // Close dialog after upload
    };

    // Handle tab change between draw and upload
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Reset state when dialog closes
    const handleClose = () => {
        setTabValue(0);
        setHasDrawnSignature(false);
        // Only clear canvas if dialog is actually open
        if (open) {
            clearCanvas();
        }
        onClose();
    };

    return (
        <Dialog 
            open={open} 
            maxWidth="md" 
            fullWidth
            TransitionProps={{
                onEntered: initializeCanvas // Initialize canvas after dialog opens
            }}
        >
            <AppDialogTitle title="Signature Capture" onClose={handleClose} />
            <DialogContent>
                <Box>
                    <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
                        <Tab label="Draw Signature" />
                        <Tab label="Upload Signature" />
                    </Tabs>

                    {/* Draw Signature Tab */}
                    {tabValue === 0 && (
                        <Box>
                            <Paper 
                                elevation={2} 
                                sx={{ 
                                    p: 2, 
                                    mb: 2,
                                    border: disabled ? '1px solid #ccc' : '2px dashed #1976d2',
                                    opacity: disabled ? 0.6 : 1
                                }}
                            >
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                    {disabled ? 'Signature drawing is disabled' : 'Draw your signature in the box below'}
                                </Typography>
                                <canvas
                                    ref={canvasRef}
                                    width={400}
                                    height={150}
                                    style={{
                                        border: '1px solid #ccc',
                                        cursor: disabled ? 'not-allowed' : 'crosshair',
                                        backgroundColor: '#fff',
                                        width: '100%',
                                        maxWidth: '400px'
                                    }}
                                    onMouseDown={startDrawing}
                                    onMouseMove={draw}
                                    onMouseUp={stopDrawing}
                                    onMouseLeave={stopDrawing}
                                />
                                <Grid container spacing={1} sx={{ mt: 1 }}>
                                    <Grid item>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<ClearIcon />}
                                            onClick={clearCanvas}
                                            disabled={disabled || !hasDrawnSignature}
                                        >
                                            Clear
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Box>
                    )}

                    {/* Upload Signature Tab */}
                    {tabValue === 1 && (
                        <Box>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                Upload signature image (JPEG/PNG files under {formatFileSize(MAX_FILE_SIZE)})
                            </Typography>
                            <AttachmentUploader
                                onUpload={handleUploadedSignature}
                                accept={
                                    ACCEPTED_IMAGE_FILE_FORMAT.reduce((acc, mimeType) => {
                                        acc[mimeType] = []
                                        return acc
                                    }, {})
                                }
                                maxFiles={MAX_NO_OF_FILES}
                                maxSize={MAX_FILE_SIZE}
                                disabled={disabled}
                            />
                        </Box>
                    )}
                </Box>
            </DialogContent>
            
            {/* Dialog Actions - Only show Accept button for drawn signatures */}
            {tabValue === 0 && (
                <DialogActions>
                    <Button onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={acceptSignature}
                        disabled={disabled || !hasDrawnSignature}
                    >
                        Accept Signature
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default SignatureDialog;